import React, { useState, useEffect, useRef } from "react";
import { PREFETCH_IMAGE_CACHE, isImageCacheAvailable } from "@/lib/prefetchStorage";

/** רשת איטית או חיסכון בנתונים — לא מריצים רענון תמונות ברקע (נשארים על קאש/לוקאל) */
function isSlowOrDataSaverConnection() {
  if (typeof navigator === "undefined") return false;
  if (!navigator.onLine) return true;
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn?.saveData) return true;
  const type = (conn?.effectiveType || "").toLowerCase();
  if (type === "2g" || type === "3g") return true;
  return false;
}

/**
 * PrefetchImage – תמונה ידידותית לאוף־ליין ול־3G.
 * 1. קודם כל מנסה למשוך מה-Cache API (אם כבר שמרנו פעם) — מוצג מיד מהלוקאל.
 * 2. אם יש רשת מהירה — מבצע עדכון שקט ברקע ושומר ב־cache.
 * 3. ב־3G/חיסכון בנתונים — לא מריצים רענון ברקע, רק מהקאש (או טעינה חד־פעמית אם אין קאש).
 */
export default function PrefetchImage({ src, ...imgProps }) {
  const [resolvedSrc, setResolvedSrc] = useState(src || null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!src) {
      setResolvedSrc(null);
      return;
    }

    if (!isImageCacheAvailable()) {
      setResolvedSrc(src);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const cache = await caches.open(PREFETCH_IMAGE_CACHE);
        if (cancelled) return;

        // 1. קודם כל מנסים מהקאש המקומי – עובד גם בלי רשת
        const cached = await cache.match(src);
        if (cancelled) return;
        if (cached) {
          const blob = await cached.blob();
          if (cancelled) return;
          const blobUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            return;
          }
          blobUrlRef.current = blobUrl;
          setResolvedSrc(blobUrl);
        } else {
          // אם אין בקאש – נשתמש ב-src הרגיל כרקע
          setResolvedSrc(src);
        }

        // 2. אם יש רשת מהירה (לא 3G/saveData) – רענון שקט ברקע; ב־3G נשארים על קאש בלבד
        const shouldRefreshInBackground = navigator.onLine && !isSlowOrDataSaverConnection();
        if (shouldRefreshInBackground) {
          try {
            const res = await fetch(src, { cache: "no-cache" });
            if (!res.ok || cancelled) return;
            const responseClone = res.clone();
            await cache.put(src, responseClone);
            const blob = await res.blob();
            if (cancelled) return;
            const freshUrl = URL.createObjectURL(blob);
            if (cancelled) {
              URL.revokeObjectURL(freshUrl);
              return;
            }
            // מעדכנים לתמונה חדשה רק אם היינו על src ישן
            if (!blobUrlRef.current) {
              blobUrlRef.current = freshUrl;
              setResolvedSrc(freshUrl);
            } else {
              // כבר השתמשנו ב-blob מהקאש – משחררים אותו ומעדכנים לחדש
              URL.revokeObjectURL(blobUrlRef.current);
              blobUrlRef.current = freshUrl;
              setResolvedSrc(freshUrl);
            }
          } catch {
            // שגיאת רשת – נשארים עם מה שיש (קאש או src)
          }
        }
      } catch {
        setResolvedSrc(src);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [src]);

  if (!resolvedSrc) return null;
  return <img src={resolvedSrc} {...imgProps} />;
}
